import React from 'react';
import { motion } from 'framer-motion';
import { Gift, Users, Package, TrendingUp, Sparkles, Award } from 'lucide-react';
import { Link } from 'react-router-dom';

const Services = () => {
  const services = [
    {
      icon: <Gift className="w-12 h-12" />,
      title: 'Corporate Gifting',
      description: 'Strengthen client and partner relationships with premium, curated corporate gifts that reflect your brand values and appreciation.',
      features: ['Luxury gift hampers', 'Custom gift boxes', 'Seasonal gifting', 'Client appreciation gifts']
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: 'Employee Welcome Kits',
      description: 'Make first impressions count with professionally designed onboarding kits that help new hires feel valued from day one.',
      features: ['Branded welcome kits', 'Tech accessories', 'Custom packaging', 'Personalized items']
    },
    {
      icon: <Package className="w-12 h-12" />,
      title: 'Promotional Merchandise',
      description: 'Amplify your marketing efforts with custom branded products that increase visibility and leave lasting impressions.',
      features: ['Brand merchandise', 'Marketing giveaways', 'Trade show items', 'Bulk orders']
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: 'Event & Conference Giveaways',
      description: 'Create memorable experiences with thoughtfully selected event merchandise and conference giveaways.',
      features: ['Conference kits', 'Delegate bags', 'Event swag', 'Booth giveaways']
    },
    {
      icon: <Sparkles className="w-12 h-12" />,
      title: 'Festive Gifting Solutions',
      description: 'Celebrate festivals and special occasions with elegant gift hampers that spread joy and strengthen bonds.',
      features: ['Diwali hampers', 'New Year gifts', 'Festival boxes', 'Custom festive packaging']
    },
    {
      icon: <Award className="w-12 h-12" />,
      title: 'Custom Branded Merchandise',
      description: 'End-to-end branding and customization services for all your merchandise needs with quality assurance.',
      features: ['Logo branding', 'Custom packaging', 'Design services', 'Quality control']
    },
  ];

  return (
    <div>
      <section className="py-24 md:py-32 bg-gradient-to-br from-surface via-background to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="text-xs font-medium tracking-widest uppercase text-muted mb-4" data-testid="services-label">
              What We Offer
            </div>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-tight mb-6" data-testid="services-title">
              Our <span className="text-accent">Services</span>
            </h1>
            <p className="text-lg md:text-xl text-muted max-w-3xl mx-auto" data-testid="services-description">
              Comprehensive corporate gifting and promotional merchandise solutions designed to elevate your brand and strengthen relationships.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative overflow-hidden bg-white border border-slate-100 p-8 rounded-xl hover:border-accent/20 hover:shadow-2xl transition-all duration-300"
                data-testid={`service-card-${index}`}
              >
                <div className="text-accent mb-4" data-testid={`service-icon-${index}`}>{service.icon}</div>
                <h2 className="text-2xl font-semibold mb-3" data-testid={`service-title-${index}`}>{service.title}</h2>
                <p className="text-muted mb-6" data-testid={`service-description-${index}`}>{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center text-sm text-muted" data-testid={`service-feature-${index}-${fIndex}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-accent mr-3"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mt-16"
          >
            <h3 className="text-2xl font-semibold mb-4" data-testid="services-cta-title">Ready to get started?</h3>
            <p className="text-muted mb-6" data-testid="services-cta-description">
              Let's discuss your requirements and create the perfect gifting solution for your business.
            </p>
            <Link
              to="/contact"
              data-testid="services-cta-button"
              className="inline-flex h-12 px-8 rounded-full bg-accent text-white font-medium hover:scale-105 transition-transform duration-200 shadow-xl shadow-accent/20 items-center justify-center"
            >
              Get in Touch
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
